import { useCallback } from 'react';

import type { FullDevice } from '@rtkwlf/asset-data-service';
import type { ResponseError } from '@rtkwlf/rendall';
import type { QueryFunction, UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import { MINUTE_IN_MS } from 'ae-constants/constants';

import { useAssetDataServiceApi } from '../use-asset-data-service-api';

export const getDeviceDetailsKey = (
  organizationId: string,
  deviceId: string,
) => [organizationId, 'ads', 'get-device-details', deviceId];

const stubDeviceDetails: FullDevice = {
  category: 'server',
  hostname: 'my.hostname',
  id: 'b2bdaee5-6f13-4867-b204-19637',
  importance: 'high',
  lastSeenTime: new Date('2021-01-01T17:32:28Z'),
  name: 'aws:085957942246',
  networkInterfaces: [
    {
      ipAddresses: ['10.171.154.142', '10.171.154.142'],
      mac: '00:50:56:9D:20:6B',
      name: 'eth0',
    },
    {
      ipAddresses: ['10.171.154.141'],
      mac: '00:50:56:9D:19:6B',
      name: 'eth0',
    },
    {
      ipAddresses: ['10.171.154.145'],
      mac: '00:50:56:9D:38:6B',
      name: 'eth0',
    },
  ],
  risk: {
    count: 1,
    score: 1.2,
    lastSuccessfulScanned: {
      agent: new Date('2024-01-01T17:32:28Z'),
    },
  },
  netbios: 'aws.netbios',
  sources: ['agent'],
  os: { name: 'CentOS', type: 'Linux', version: '' },
  software: [
    {
      name: 'Microsoft WebView2 Runtime',
      vendor: 'Microsoft Corporation',
      version: '117.0.2045.36',
    },
    {
      name: 'Arctic Wolf Agent 2023-02_130',
      vendor: 'Arctic Wolf Networks',
      version: '23.2.130',
    },
    {
      name: 'Microsoft Visual C++ 2019 X86 Additional Resource',
      vendor: 'Microsoft Corporation',
      version: '14.28.29913',
    },
    {
      name: 'Microsoft OneDrive',
      vendor: 'Microsoft Corporation',
      version: '23.180.0828.0001',
    },
    {
      name: 'Software Name 1',
      vendor: 'Vendor 1',
      version: '1',
    },
    {
      name: 'Software Name 2',
      vendor: 'Vendor 2',
      version: '2',
    },
    {
      name: 'Software Name 3',
      vendor: 'Vendor 3',
      version: '3',
    },
    {
      name: 'Software Name 4',
      vendor: 'Vendor 4',
      version: '4',
    },
  ],
  tags: ['lorem_sql_admin', 'server', 'iot', 'backup_recovery', 'iam'],
};

const mockQueryFn = () =>
  new Promise<FullDevice>((resolve) => {
    setTimeout(() => resolve(stubDeviceDetails), 500);
  });

export function useDeviceDetails<Data = FullDevice>(
  deviceId: string,
  options?: Omit<
    UseQueryOptions<FullDevice, ResponseError, Data>,
    'queryKey' | 'queryFn'
  >,
) {
  const api = useAssetDataServiceApi();
  const { id } = useSelectedOrganization();

  const [fullMockedFF] = useFeatureFlag('ADS_FULL_MOCKED');

  const queryFn: QueryFunction<FullDevice> = useCallback(() => {
    if (fullMockedFF) {
      return mockQueryFn();
    }

    return api.getDevice({
      organizationId: id,
      deviceId,
    });
  }, [api, deviceId, fullMockedFF, id]);

  return useQuery({
    queryFn,
    queryKey: getDeviceDetailsKey(id, deviceId),
    staleTime: 5 * MINUTE_IN_MS,
    ...options,
  });
}
